'use client'

import { useSignIn, useSignUp } from '@clerk/nextjs'
import { useMountEffect } from '@react-hookz/web'
import { Button } from '@snowball-tech/fractal/dist/src/components/Button'
import { InputText } from '@snowball-tech/fractal/dist/src/components/InputText'
import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'
import { cj } from '@snowball-tech/fractal/dist/src/styles/helpers'
import { UilArrowRight as ArrowRightIcon } from '@tooni/iconscout-unicons-react'
import Image, { type StaticImageData } from 'next/image'
import { useSearchParams } from 'next/navigation'

import {
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'

import constant from 'lodash/fp/constant'
import isEmpty from 'lodash/fp/isEmpty'
import isNil from 'lodash/fp/isNil'
import isNumber from 'lodash/fp/isNumber'
import isObject from 'lodash/fp/isObject'
import isString from 'lodash/fp/isString'

import { Plans } from '../enums'
import { getReturnUrl, isValidEmail } from '../helpers'
import { signup } from '../helpers/authentication'
import { useRouter } from '../helpers/translations'
import useApi from '../hooks/useApi'
import useFeatureFlags from '../hooks/useFeatureFlags'
import useLogger from '../hooks/useLogger'
import useTracking from '../hooks/useTracking'
import useTranslations from '../hooks/useTranslations'
import ImageOopsSnowie from '../images/OopsSnowie.png'
import ImageSnowieSendMagic from '../images/SnowieSendMagic.png'
import GoogleConnectButton from './GoogleConnectButton'
import Separator from './Separator'

type Props = {
  buttonLabel?: string
  buttonPosition?: 'below' | 'inline'
  darkSeparator?: boolean
  editionUuid?: string
  fullWidth?: boolean
  highlightLabel?: boolean
  iconOnly?: boolean
  label?: string | false
  origin?: 'free_subscribe' | 'header' | 'hero' | 'home' | 'pricing'
  redirectOnSuccess?: boolean
  reloadOnSuccess?: boolean
  returnUrl?: string
  sendSignupEmail?: boolean
  sendVerificationEmail?: boolean
  submittedContent?: ReactNode
  submittedImage?: string | false | StaticImageData
  submittedImageWidth?: number
  submittedTitle?: string | false
  withRgpdInfo?: boolean
  onDone?: (result: boolean | null | void) => void
  onExpire?: () => void
  onFailure?: () => void
  onSubmit?: () => void
  onSubmitted?: () => void
  onSuccess?: () => void
}
export default function SubscribeForm({
  buttonLabel,
  buttonPosition = 'below',
  darkSeparator = false,
  editionUuid,
  fullWidth = true,
  highlightLabel = false,
  iconOnly = false,
  label,
  onDone,
  onExpire,
  onFailure,
  onSubmit,
  onSubmitted,
  onSuccess,
  origin,
  redirectOnSuccess = true,
  reloadOnSuccess = true,
  returnUrl,
  sendSignupEmail = true,
  sendVerificationEmail = false,
  submittedContent,
  submittedImage,
  submittedImageWidth,
  submittedTitle,
  withRgpdInfo = true,
}: Props) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const $t = useTranslations()

  const { allowGoogleAuth } = useFeatureFlags()

  const endpoints = useApi()

  const { track } = useTracking()

  const logger = useLogger()

  const {
    isLoaded: isSignupLoaded,
    setActive: setSignupActive,
    signUp: clerkSignup,
  } = useSignUp()
  const { startEmailLinkFlow: startSignupEmailLinkFlow } = sendSignupEmail
    ? (clerkSignup?.createEmailLinkFlow() ?? {})
    : { startEmailLinkFlow: undefined }

  const {
    isLoaded: isSigninLoaded,
    setActive: setSigninActive,
    signIn: clerkSignin,
  } = useSignIn()
  const { startEmailLinkFlow: startSigninEmailLinkFlow } =
    clerkSignin?.createEmailLinkFlow() ?? {}

  const [email, setEmail] = useState(
    decodeURIComponent(searchParams.get('email') ?? '')?.replace(/\s/g, '+') ??
      '',
  )
  const isEmailOriginallyEmpty = useRef(isEmpty(email))

  const validity = isEmailOriginallyEmpty.current || isValidEmail(email)

  const [error, setError] = useState(!validity)
  const [success, setSuccess] = useState(
    !isEmailOriginallyEmpty.current && validity,
  )

  const [isActionDisabled, setIsActionDisabled] = useState(
    isEmpty(email) || !validity,
  )

  const [isLoading, setIsLoading] = useState(false)
  const [isVerifying, setIsVerifying] = useState(
    searchParams.get('message') === 'signup-successfull',
  )
  const [isExpired, setIsExpired] = useState(false)
  const [signupError, setSignupError] = useState<string | true | null>(
    searchParams.get('message') === 'unable-to-signup' ? true : null,
  )

  const actualReturnUrl = getReturnUrl(
    returnUrl ||
      decodeURIComponent(searchParams.get('return') ?? '')?.replace(/\s/g, '+'),
  )

  useMountEffect(() => {
    if (searchParams.get('message') === 'signup-successfull') {
      onSubmitted?.()
    }
  })

  const fromEditionUuid = editionUuid || searchParams.get('from') || undefined
  const utmData: Record<string, string> = useMemo(
    () =>
      Array.from(searchParams.entries()).reduce(
        (acc, [key, value]) => {
          if (key.startsWith('utm_')) {
            acc[key.replace('utm_', '')] = value
          }

          return acc
        },
        {} as Record<string, string>,
      ),
    [searchParams],
  )

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const submit = useCallback(async () => {
    if (isActionDisabled || !isValidEmail(email)) {
      return false
    }

    onSubmit?.()

    setSignupError(null)
    setIsLoading(true)

    const result = await signup({
      email,
      logger,
      postSignupUrl: actualReturnUrl,
      redirect: router.push,
      setIsExpired: (newValue: boolean) => {
        if (newValue) {
          onExpire?.()
        }

        setIsExpired(newValue)
      },
      setIsVerifying: (newValue: boolean) => {
        if (newValue) {
          timeoutRef.current = setTimeout(() => {
            onSubmitted?.()
          }, 1000)
        }

        setIsVerifying(newValue)
      },
      signin: {
        clerkSignin,
        isLoaded: isSigninLoaded,
        setActive: setSigninActive,
        startEmailLinkFlow: startSigninEmailLinkFlow,
      },
      signup: {
        clerkSignup,
        isLoaded: isSignupLoaded,
        prepareEmailAddressVerification: sendVerificationEmail
          ? clerkSignup?.prepareEmailAddressVerification
          : undefined,
        register: async (emailToSignup) =>
          endpoints.users.register(
            emailToSignup,
            Plans.Free,
            fromEditionUuid,
            utmData,
            track,
            isEmpty(origin) && !isEmpty(fromEditionUuid)
              ? 'free_subscribe'
              : origin,
          ),
        setActive: setSignupActive,
        startEmailLinkFlow: sendSignupEmail
          ? startSignupEmailLinkFlow
          : undefined,
      },
    })

    onDone?.(isObject(result) && !isNil(result) ? result.result : result)

    setIsLoading(constant(false))

    if (result === true) {
      onSuccess?.()

      if (isEmpty(actualReturnUrl)) {
        if (reloadOnSuccess) {
          window.location.reload()
        }
      } else if (redirectOnSuccess) {
        if (actualReturnUrl.startsWith('http')) {
          window.location.assign(actualReturnUrl)
        } else {
          window.location.assign(`${window.location.origin}${actualReturnUrl}`)
        }
      }
    } else {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      onFailure?.()

      setIsExpired(constant(false))
      setIsVerifying(constant(false))
      setSignupError(constant(result?.error?.message || true))
    }

    return result
  }, [
    actualReturnUrl,
    clerkSignin,
    clerkSignup,
    email,
    endpoints.users,
    fromEditionUuid,
    isActionDisabled,
    isSigninLoaded,
    isSignupLoaded,
    logger,
    onDone,
    onExpire,
    onFailure,
    onSubmit,
    onSubmitted,
    onSuccess,
    origin,
    redirectOnSuccess,
    reloadOnSuccess,
    router.push,
    sendSignupEmail,
    sendVerificationEmail,
    setSigninActive,
    setSignupActive,
    startSigninEmailLinkFlow,
    startSignupEmailLinkFlow,
    track,
    utmData,
  ])

  const handleResendLink = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()

    submit()
  }

  const handleEmailChange = (
    _event: ChangeEvent<HTMLInputElement>,
    newEmail: string,
  ) => {
    setEmail(newEmail)

    if (isValidEmail(newEmail)) {
      setIsActionDisabled(false)
      setSuccess(true)
      setError(false)
    } else {
      setIsActionDisabled(true)
      setSuccess(false)
      setError(true)
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      submit()
    }
  }

  return (
    <div className="flex w-full flex-col items-end gap-2">
      {!isVerifying && (
        <div className={cj('flex flex-col gap-1', fullWidth ? 'w-full' : '')}>
          <div
            className={cj(
              'flex flex-row items-end gap-1',
              fullWidth ? 'w-full' : '',
            )}
          >
            <InputText
              aria-label={
                label || $t.notEmpty('subscribe.form.signup.input.label')
              }
              autoFocus={searchParams.get('message') === 'unable-to-signup'}
              button={{
                icon: <ArrowRightIcon />,
                iconOnly,
                label: buttonLabel || $t('subscribe.form.signup.button.label'),
              }}
              className={highlightLabel ? '[&_label]:font-weight-bold' : ''}
              disabled={isLoading}
              error={error}
              fullWidth
              label={
                label === false
                  ? undefined
                  : label || $t.notEmpty('subscribe.form.signup.input.label')
              }
              placeholder={$t('signup.form.input.label')}
              success={success}
              type="email"
              value={email}
              withButton={buttonPosition === 'inline'}
              onButtonClick={buttonPosition === 'inline' ? submit : undefined}
              onChange={handleEmailChange}
              onKeyDown={handleKeyDown}
            />
          </div>
          {withRgpdInfo && (
            <Typography variant="caption-median">
              {$t.rich.notEmpty('signup.form.input.description')}
            </Typography>
          )}

          {allowGoogleAuth && (
            <>
              <Separator
                className={darkSeparator ? 'bg-grey-30' : ''}
                label={$t('general.or')}
                labelClassName={darkSeparator ? 'text-grey-30' : 'text-grey-70'}
              />

              <GoogleConnectButton
                fullWidth
                label={$t('signup.form.google.button.label')}
                metadata={{ origin }}
                returnUrl={actualReturnUrl}
              />
            </>
          )}
        </div>
      )}

      {isVerifying && (
        <div className="flex flex-col w-full items-center gap-3 self-center">
          {(submittedTitle !== false || isExpired) && (
            <Typography className="text-center" element="p" variant="heading-3">
              {isExpired
                ? $t('signup.content.link_expired')
                : submittedTitle || $t('signup.content.check_email_link')}
            </Typography>
          )}

          {(submittedImage !== false || isExpired) && (
            <Image
              alt=""
              src={
                isExpired
                  ? ImageOopsSnowie
                  : submittedImage || ImageSnowieSendMagic
              }
              width={
                isNumber(submittedImageWidth) &&
                !Number.isNaN(submittedImageWidth)
                  ? submittedImageWidth
                  : 166
              }
            />
          )}

          {(submittedContent !== false || isExpired) &&
            (isString(submittedContent) || isNil(submittedContent) ? (
              <Typography
                className="text-center"
                variant={isExpired ? 'body-1-link' : 'body-1'}
                onClick={isExpired ? handleResendLink : undefined}
              >
                {isExpired
                  ? $t('signup.content.resend_link')
                  : submittedContent ||
                    $t('signup.content.open_and_click_link')}
              </Typography>
            ) : (
              submittedContent
            ))}
        </div>
      )}

      {!isNil(signupError) && (
        <Typography
          className="text-center self-center my-2"
          element="div"
          variant="body-1-bold"
        >
          {isString(signupError)
            ? signupError.toLowerCase().includes('disposable email')
              ? $t.rich('subscribe.form.signup.disposable_email_error')
              : $t.rich('subscribe.form.signup.detailled_error', {
                  error: signupError,
                })
            : $t.rich('subscribe.form.signup.error')}
        </Typography>
      )}

      {buttonPosition !== 'inline' && !isVerifying && (
        <Button
          icon={<ArrowRightIcon />}
          iconOnly={iconOnly}
          label={buttonLabel || $t('subscribe.form.signup.button.label')}
          onClick={submit}
        />
      )}
    </div>
  )
}
